// Handles all job application submissions with database integration, error handling, and UK market optimization
// FIXED: Updated to use VPS server instead of localhost

import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system';
import * as MailComposer from 'expo-mail-composer';
import { Alert } from 'react-native';

class SubmissionService {
  constructor() {
    // FIXED: Consolidated to use VPS server only
    this.baseURL = 'https://api.jobeng.ai'; // VPS server endpoint
    this.apiURL = this.baseURL; // Always use VPS server
    
    // UK-specific configuration
    this.ukConfig = {
      currency: 'GBP',
      currencySymbol: '£',
      dateFormat: 'DD/MM/YYYY',
      timeZone: 'Europe/London',
      phoneFormat: '+44',
      locations: [
        'London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow',
        'Sheffield', 'Bradford', 'Liverpool', 'Edinburgh', 'Bristol',
        'Cardiff', 'Coventry', 'Leicester', 'Sunderland', 'Belfast',
        'Newcastle', 'Brighton', 'Hull', 'Plymouth', 'Stoke-on-Trent',
        'Wolverhampton', 'Derby', 'Swansea', 'Southampton', 'Salford',
        'Aberdeen', 'Westminster', 'Portsmouth', 'York', 'Peterborough',
        'Dundee', 'Lancaster', 'Oxford', 'Newport', 'Preston',
        'St Albans', 'Norwich', 'Chester', 'Cambridge', 'Salisbury'
      ]
    };
    
    // Request configuration
    this.timeout = 15000;
    this.retryAttempts = 2;
    this.retryDelay = 1500;
    
    console.log(`🌐 SubmissionService initialized with VPS: ${this.apiURL}`);
  }

  // Initialize submission service
  async initialize() {
    try {
      // Check network connectivity
      await this.checkNetworkConnectivity();
      
      // Initialize local storage
      await this.initializeLocalStorage();
      
      // Sync pending submissions
      await this.syncPendingSubmissions();
      
      console.log('✅ SubmissionService initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize SubmissionService:', error);
      return false;
    }
  }

  // Check network connectivity
  async checkNetworkConnectivity() {
    try {
      const response = await fetch(`${this.apiURL}/health`, {
        method: 'GET',
        timeout: 5000,
      });
      
      if (!response.ok) {
        throw new Error('API not accessible');
      }
      
      console.log('✅ VPS server connectivity confirmed');
      return true;
    } catch (error) {
      console.warn('⚠️ Network connectivity issue:', error.message);
      return false;
    }
  }

  // Initialize local storage for offline submissions
  async initializeLocalStorage() {
    try {
      const pendingSubmissions = await AsyncStorage.getItem('pendingSubmissions');
      if (!pendingSubmissions) {
        await AsyncStorage.setItem('pendingSubmissions', JSON.stringify([]));
      }
      
      const submissionHistory = await AsyncStorage.getItem('submissionHistory');
      if (!submissionHistory) {
        await AsyncStorage.setItem('submissionHistory', JSON.stringify([]));
      }
      
      const userPreferences = await AsyncStorage.getItem('userPreferences');
      if (!userPreferences) {
        await AsyncStorage.setItem('userPreferences', JSON.stringify({
          autoSave: true,
          notifications: true,
          emailConfirmation: true,
          dataRetention: 30 // days
        }));
      }
    } catch (error) {
      console.error('Failed to initialize local storage:', error);
      throw error;
    }
  }

  // Get authentication headers
  async getAuthHeaders() {
    try {
      const token = await AsyncStorage.getItem('token') || await AsyncStorage.getItem('auth_token');
      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      };
    } catch (error) {
      console.error('❌ Error getting auth headers:', error);
      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
    }
  }

  // Make API request with retry logic
  async makeRequest(endpoint, options = {}) {
    const url = `${this.apiURL}${endpoint}`;
    const headers = await this.getAuthHeaders();
    
    const requestOptions = {
      timeout: this.timeout,
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    };

    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        console.log(`🔄 Submission API Request (Attempt ${attempt}): ${options.method || 'GET'} ${endpoint}`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        
        const response = await fetch(url, {
          ...requestOptions,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const responseText = await response.text();
        let data;
        
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.warn('⚠️ Non-JSON response received:', responseText);
          data = { message: responseText, success: true };
        }
        
        console.log(`✅ Submission API Success: ${options.method || 'GET'} ${endpoint}`);
        return data;
        
      } catch (error) {
        console.error(`❌ Submission API Error (Attempt ${attempt}): ${error.message}`);
        
        if (attempt === this.retryAttempts) {
          if (error.name === 'AbortError' || error.message.includes('timeout')) {
            throw new Error('Request timeout. Please check your connection.');
          } else if (error.message.includes('Network request failed')) {
            throw new Error('Network error. Please check your internet connection.');
          } else {
            throw error;
          }
        }
        
        // Wait before retry
        await this.delay(this.retryDelay * attempt);
      }
    }
  }

  // Delay utility
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Submit job application
  async submitJobApplication(applicationData) {
    try {
      console.log('📝 Submitting job application...');
      
      // Validate required fields
      this.validateApplicationData(applicationData);
      
      // Enhance with UK-specific data
      const enhancedData = this.enhanceWithUKData(applicationData);
      
      // Submit to VPS server
      const response = await this.makeRequest('/api/applications', {
        method: 'POST',
        body: JSON.stringify(enhancedData),
      });
      
      if (response.success) {
        // Store in submission history
        await this.addToSubmissionHistory(enhancedData, response);
        
        console.log('✅ Job application submitted successfully');
        return response;
      } else {
        throw new Error(response.error || 'Submission failed');
      }
      
    } catch (error) {
      console.error('❌ Job application submission failed:', error);
      
      // Store for offline submission
      await this.addToPendingSubmissions(applicationData);
      
      throw error;
    }
  }

  // Validate application data
  validateApplicationData(data) {
    const required = ['jobId', 'applicantName', 'email', 'phone'];
    const missing = required.filter(field => !data[field]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      throw new Error('Invalid email format');
    }
    
    // Validate UK phone format
    const ukPhoneRegex = /^(\+44|0)[1-9]\d{8,9}$/;
    if (!ukPhoneRegex.test(data.phone)) {
      console.warn('⚠️ Phone number may not be in UK format');
    }
  }

  // Enhance data with UK-specific information
  enhanceWithUKData(data) {
    return {
      ...data,
      currency: this.ukConfig.currency,
      timeZone: this.ukConfig.timeZone,
      submissionDate: new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      submissionTime: new Date().toLocaleTimeString('en-GB'),
      region: 'UK',
      dataProtectionConsent: true, // GDPR compliance
    };
  }

  // Add to pending submissions for offline processing
  async addToPendingSubmissions(data) {
    try {
      const pending = await AsyncStorage.getItem('pendingSubmissions');
      const submissions = pending ? JSON.parse(pending) : [];
      
      submissions.push({
        ...data,
        id: Date.now(),
        timestamp: new Date().toISOString(),
        status: 'pending'
      });
      
      await AsyncStorage.setItem('pendingSubmissions', JSON.stringify(submissions));
      console.log('📱 Added to pending submissions for offline processing');
    } catch (error) {
      console.error('Failed to add to pending submissions:', error);
    }
  }

  // Add to submission history
  async addToSubmissionHistory(data, response) {
    try {
      const history = await AsyncStorage.getItem('submissionHistory');
      const submissions = history ? JSON.parse(history) : [];
      
      submissions.push({
        ...data,
        submissionId: response.data?.id || Date.now(),
        timestamp: new Date().toISOString(),
        status: 'submitted',
        response: response
      });
      
      // Keep only last 100 submissions
      if (submissions.length > 100) {
        submissions.splice(0, submissions.length - 100);
      }
      
      await AsyncStorage.setItem('submissionHistory', JSON.stringify(submissions));
    } catch (error) {
      console.error('Failed to add to submission history:', error);
    }
  }

  // Sync pending submissions when back online
  async syncPendingSubmissions() {
    try {
      const pending = await AsyncStorage.getItem('pendingSubmissions');
      if (!pending) return;
      
      const submissions = JSON.parse(pending);
      if (submissions.length === 0) return;
      
      console.log(`🔄 Syncing ${submissions.length} pending submissions...`);
      
      const successful = [];
      const failed = [];
      
      for (const submission of submissions) {
        try {
          const response = await this.submitJobApplication(submission);
          successful.push(submission.id);
          console.log(`✅ Synced submission: ${submission.id}`);
        } catch (error) {
          failed.push(submission.id);
          console.error(`❌ Failed to sync submission: ${submission.id}`, error);
        }
      }
      
      // Remove successful submissions from pending
      const remainingSubmissions = submissions.filter(s => !successful.includes(s.id));
      await AsyncStorage.setItem('pendingSubmissions', JSON.stringify(remainingSubmissions));
      
      console.log(`✅ Sync complete: ${successful.length} successful, ${failed.length} failed`);
      
    } catch (error) {
      console.error('Failed to sync pending submissions:', error);
    }
  }

  // Get submission history
  async getSubmissionHistory() {
    try {
      const history = await AsyncStorage.getItem('submissionHistory');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Failed to get submission history:', error);
      return [];
    }
  }

  // Get pending submissions
  async getPendingSubmissions() {
    try {
      const pending = await AsyncStorage.getItem('pendingSubmissions');
      return pending ? JSON.parse(pending) : [];
    } catch (error) {
      console.error('Failed to get pending submissions:', error);
      return [];
    }
  }

  // Clear submission data
  async clearSubmissionData() {
    try {
      await AsyncStorage.removeItem('pendingSubmissions');
      await AsyncStorage.removeItem('submissionHistory');
      console.log('✅ Submission data cleared');
    } catch (error) {
      console.error('Failed to clear submission data:', error);
    }
  }

  // Get UK locations for autocomplete
  getUKLocations() {
    return this.ukConfig.locations;
  }

  // Format UK phone number
  formatUKPhone(phone) {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    
    // Handle different UK phone formats
    if (digits.startsWith('44')) {
      return '+' + digits;
    } else if (digits.startsWith('0')) {
      return '+44' + digits.substring(1);
    } else if (digits.length === 10) {
      return '+44' + digits;
    }
    
    return phone; // Return original if can't format
  }

  // Validate UK postcode
  validateUKPostcode(postcode) {
    const ukPostcodeRegex = /^[A-Z]{1,2}[0-9R][0-9A-Z]? [0-9][ABD-HJLNP-UW-Z]{2}$/i;
    return ukPostcodeRegex.test(postcode.replace(/\s/g, ' ').toUpperCase());
  }

  // Get service configuration
  getConfig() {
    return {
      apiURL: this.apiURL,
      timeout: this.timeout,
      retryAttempts: this.retryAttempts,
      retryDelay: this.retryDelay,
      ukConfig: this.ukConfig
    };
  }
}

// Export singleton instance
export default new SubmissionService();
